"use client";

/**
 * Product selection grid with fit reasons.
 *
 * Extracted from `compass-intake.jsx:644-662`.
 * Shows products in a bordered card list with checkboxes and fit reason tags.
 */

import { Check } from "../lib/icons";

export interface ProductGridProduct {
  name: string;
  category: string;
  bg: string;
  fg: string;
  mark: string;
  /** Fit reasons displayed as tags. */
  fit?: string[];
}

export interface ProductGridProps {
  /** Products to display. */
  products: ProductGridProduct[];
  /** Boolean array indicating selected state per index. */
  selected?: boolean[];
  /** Called when a product is toggled. */
  onToggle?: (index: number) => void;
}

/**
 * Product selection grid: bordered table-like list with checkboxes and fit tags.
 */
export function ProductGrid({
  products,
  selected = [],
  onToggle,
}: ProductGridProps) {
  return (
    <div
      className={[
        "border border-(--border-default)",
        "rounded-(--radius-container)",
        "overflow-hidden bg-(--bg-white)",
        "shadow-(--shadow-xs)",
      ].join(" ")}
    >
      {products.map((p, i) => {
        const isOn = selected[i] ?? false;
        return (
          <button
            key={i}
            type="button"
            className={[
              "flex gap-3 items-center text-left w-full",
              "border-0 border-b border-b-(--border-subtle) last:border-b-0",
              "px-[15px] py-[12px]",
              "cursor-pointer transition-colors duration-300",
              "font-[family-name:var(--font-body)]",
              isOn
                ? "bg-(--bg-focused) shadow-[inset_3px_0_0_var(--fg-brand)]"
                : "bg-(--bg-white) hover:bg-(--bg-sky)",
            ].join(" ")}
            onClick={() => onToggle?.(i)}
          >
            {/* Checkbox */}
            <span
              className={[
                "flex-shrink-0 w-[18px] h-[18px]",
                "rounded-[5px] border-[1.5px]",
                "flex items-center justify-center",
                isOn
                  ? "bg-(--brand-navy) border-(--brand-navy) text-white"
                  : "border-(--border-strong) text-transparent",
              ].join(" ")}
            >
              <Check size={13} />
            </span>

            {/* Logo */}
            <span
              className="flex-shrink-0 w-[30px] h-[30px] rounded-(--radius-sm) flex items-center justify-center text-[14px] font-bold"
              style={{ background: p.bg, color: p.fg }}
            >
              {p.mark}
            </span>

            {/* Text */}
            <span className="flex flex-col gap-[2px] flex-1 min-w-0">
              <span className="flex items-baseline gap-[9px]">
                <span className="text-[13.5px] font-semibold text-(--fg-primary)">
                  {p.name}
                </span>
                <span className="text-[11.5px] text-(--fg-muted)">
                  {p.category}
                </span>
              </span>
              {p.fit && p.fit.length > 0 && (
                <span className="flex items-center gap-[6px] flex-wrap mt-[2px]">
                  {p.fit.map((f, fi) => (
                    <span
                      key={fi}
                      className="text-[10.5px] font-medium text-(--fg-secondary) bg-(--bg-canvas) border border-(--border-subtle) rounded-full px-[9px] py-[3px] whitespace-nowrap"
                    >
                      {f}
                    </span>
                  ))}
                </span>
              )}
            </span>
          </button>
        );
      })}
    </div>
  );
}
